import React, { useState, useEffect } from "react";
import {
  AcademicYearDTO,
  ClassName,
  UserScheduleResponse,
  LookupUserDTOV1,
} from "@beable/roster-backend-client-v1";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../styles";
import courseService from "../../../services/CourseService";
import userSupportService from '../../../services/UserSupportService';
import AcademicYearSelector from "../../Common/AcademicYearSelector";
import UserFeedData from "./UserSubSections/UserFeedData";
import UserSectionData from "./UserSubSections/UserSectionData";
import UserLessonScheduleData from "./UserSubSections/UserLessonScheduleData";
import UserCourseData from "./UserSubSections/UserCourseData";
import UserProvisionedClassesData from "./UserSubSections/UserProvisionedClassesData";

interface UserProvisioningDataProps {
  user: LookupUserDTOV1;
  userRole: string | null;
  siteId: number;
  orgId: string | undefined;
}

export default function UserProvisioningData({
  user,
  userRole,
  siteId,
  orgId,
}: UserProvisioningDataProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [academicYear, setAcademicYear] = useState<AcademicYearDTO>();

  const [provisionedClasses, setProvisionedClasses] = useState<
    ClassName[] | undefined
  >([]);
  const [userSchedule, setUserSchedule] = useState<
    UserScheduleResponse | undefined
  >();

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && orgId && academicYear) {
        const scheduleResp = await courseService.getUserScheduleData(
          siteId,
          user.userId,
          user.id,
          orgId,
          academicYear.academicYearId,
          user.primaryLanguage
        );
        setUserSchedule(scheduleResp);
      }
    };
    fetchUserData();
  }, [user, siteId, orgId, academicYear]);

  function AdditionalDropdowns() {
    return (
      <Grid container sx={styles.schedule_sectionContainer}>
        <Grid size={5}>
          <AcademicYearSelector
            siteId={siteId}
            handleSelection={(academicYear: AcademicYearDTO | undefined) => { setAcademicYear(academicYear); }}
            variant="outlined"
          />
        </Grid>
      </Grid>
    );
  }

  const isLoginTicket = !user.userAccountId;

  if (isLoginTicket)
  return (<span> This information is only available for claimed accounts</span>);
  
  return (
    <Box sx={{ padding: 10 }}>
      {AdditionalDropdowns()}
      {orgId && academicYear && (
        <>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="rostered-classes"
              id="rostered-classes"
            >
              <Typography sx={styles.schedule_subHeader}>
                Provisioned Classes
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.schedule_sectionContainer}>
              <UserProvisionedClassesData
                siteId={siteId}
                academicYearId={academicYear.academicYearId}
                userId={user.userId}
                classNames={userSchedule?.userClasses}
                setProvisionedClasses={setProvisionedClasses}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="course-list"
              id="course-list"
            >
              <Typography sx={styles.schedule_subHeader}>
                Courses Scheduled
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.schedule_sectionContainer}>
              <UserCourseData
                user={userSupportService.toUserAccountDTO(user)}
                userRole={userRole}
                assignedCourses={userSchedule?.assignedContentCourses}
                siteId={siteId}
                academicYearId={academicYear.academicYearId}
                userProvisioned={!!provisionedClasses?.length}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="lessons-list"
              id="lessons-list"
            >
              <Typography sx={styles.schedule_subHeader}>
                Lessons Schedule
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={
                userSchedule?.assignedLessons.length
                  ? styles.accordionLessonSchedule
                  : styles.schedule_sectionContainer
              }
            >
              <UserLessonScheduleData userSchedule={userSchedule} />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="assigned-sections"
              id="assigned-sections"
            >
              <Typography sx={styles.schedule_subHeader}>
                Assigned Sections
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.schedule_sectionContainer}>
              <UserSectionData
                assignedSections={userSchedule?.assignedSections}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="special-interest-feed"
              id="special-interest-feed"
            >
              <Typography sx={styles.schedule_subHeader}>
                Special Interest Feed
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.schedule_sectionContainer}>
              <UserFeedData user={userSupportService.toUserAccountDTO(user)} siteId={siteId} />
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
